import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TbAlertTriangle } from "react-icons/tb";
import { RegisterFormData } from "../registerTypes";
import { normalizeIban, validateIban } from "src/helpers/sanitizeInput";
import { lookupBicFromIban } from "src/services/bicLookup";

interface Props {
    data: RegisterFormData;
    setData: React.Dispatch<React.SetStateAction<RegisterFormData>>;
}

export default function StepBanking({ data, setData }: Props) {
    const { t } = useTranslation();
    const [ibanError, setIbanError] = useState<string | null>(null);

    const set = <K extends keyof RegisterFormData>(k: K, v: RegisterFormData[K]) =>
        setData(d => ({ ...d, [k]: v }));

    const handleIbanBlur = async (raw: string) => {
        const normalized = normalizeIban(raw);
        set("iban", normalized);
        if (normalized && !validateIban(normalized)) {
            setIbanError(t("register.errorIbanFormat"));
        } else {
            setIbanError(null);
            if (normalized && !data.bic.trim()) {
                const bic = await lookupBicFromIban(normalized);
                if (bic) setData(d => d.bic.trim() ? d : { ...d, bic });
            }
        }
    };

    return (
        <div className="register-step">
            <p className="register-plan-notice register-plan-notice--important">
                <TbAlertTriangle className="register-plan-notice__icon" />
                <span>{t("register.sepaNotice")}</span>
            </p>
            <div className="form-group">
                <label>{t("register.iban")} *</label>
                <input
                    type="text"
                    value={data.iban}
                    onChange={e => { set("iban", e.target.value.toUpperCase()); setIbanError(null); }}
                    onBlur={e => handleIbanBlur(e.target.value)}
                    placeholder="DE00 0000 0000 0000 0000 00"
                    autoFocus
                />
                {ibanError && <span className="register-step__field-error">{ibanError}</span>}
            </div>

            <div className="register-step__row">
                <div className="form-group register-step__half">
                    <label>{t("register.bic")}</label>
                    <input
                        type="text"
                        value={data.bic}
                        onChange={e => set("bic", e.target.value.toUpperCase())}
                        placeholder="DEUTDEFFXXX"
                    />
                </div>
                <div className="form-group register-step__half">
                    <label>{t("register.taxNumber")}</label>
                    <input
                        type="text"
                        value={data.taxNumber}
                        onChange={e => set("taxNumber", e.target.value)}
                        placeholder="1234567890"
                    />
                </div>
            </div>

            <label className="register-sepa-consent">
                <input
                    type="checkbox"
                    checked={data.ibanConsent}
                    onChange={e => set("ibanConsent", e.target.checked)}
                />
                <span>{t("register.sepaConsentText")}</span>
            </label>
        </div>
    );
}
